import { articlePath, type Article } from '@/utils/content';
import ArticleLink from '@/components/feature/ArticleLink';

type ArticleCardProps = {
  article: Article;
  featured?: boolean;
};

export default function ArticleCard({ article, featured = false }: ArticleCardProps) {
  if (featured) {
    return (
      <ArticleLink
        to={articlePath(article.slug)}
        className="group grid grid-cols-1 overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300 lg:grid-cols-2"
      >
        <div className="relative h-56 w-full overflow-hidden lg:h-full lg:min-h-[320px]">
          <img
            src={article.heroImage}
            alt={`${article.title} - เรียน Forex`}
            title={`${article.title} - Forex Academy`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center p-6 md:p-9">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            {article.category}
          </span>
          <h3 className="mt-4 font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-2xl">
            {article.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">{article.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-sm" />
              {article.readingTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-bar-chart-line text-sm" />
              ระดับ {article.level}
            </span>
            <span className="flex items-center gap-1.5 font-medium text-primary-700">
              อ่านบทความ
              <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </ArticleLink>
    );
  }

  return (
    <ArticleLink
      to={articlePath(article.slug)}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden">
        <img
          src={article.heroImage}
          alt={`${article.title} - เรียน Forex`}
          title={`${article.title} - Forex Academy`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
          {article.category}
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{article.description}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-sm" />
            {article.readingTime}
          </span>
          <span className="flex items-center gap-1.5 text-secondary-900">
            <i className="ri-bar-chart-line text-sm" />
            {article.level}
          </span>
        </div>
      </div>
    </ArticleLink>
  );
}